/**
 * @fileoverview Translation utility functions for the application
 * 
 * This module provides helper functions and utilities for working with
 * translations and i18n in a consistent way across the application.
 */
import { useTranslation as useReactI18nextTranslation } from 'react-i18next';
import i18n from './i18n';
import { RTL_LOCALES } from './i18n';

/**
 * Translate a string using the current locale
 * This is a wrapper around i18n.t() for simpler usage outside of React components
 * 
 * @param message - The message to translate
 * @param values - Optional values for interpolation
 * @returns Translated string
 */
export function translate(message: string, values?: Record<string, any>): string {
  if (!i18n) {
    console.warn('[Translation] i18n not initialized');
    return message;
  }
  
  try {
    // We need to cast the result to string as i18next's type definitions are complex
    const result = i18n.t(message, values ?? {});
    return typeof result === 'string' ? result : String(message);
  } catch (error) {
    console.error(`[Translation] Error translating: ${message}`, error);
    return message;
  }
}

/**
 * React hook for translation in functional components
 * Wrapper around react-i18next's useTranslation hook
 */
export function useTranslation() {
  const { t, i18n: i18nInstance } = useReactI18nextTranslation();
  
  return {
    t,
    i18n: i18nInstance,
    translate: (message: string, values?: Record<string, any>): string => {
      const result = t(message, values ?? {});
      return typeof result === 'string' ? result : String(message);
    }
  };
}

/**
 * Formats a message with plural forms
 * 
 * @param options - Plural options
 * @returns Translated string with proper plural form
 */
export function plural(options: {
  value: number;
  one: string;
  other: string;
  many?: string;
  zero?: string;
}): string {
  const { value, zero, one, many, other } = options;
  
  // Using a more nuanced approach for languages with complex plural rules
  if (value === 0 && zero) {
    return translate(zero, { count: value });
  }
  
  if (value === 1) {
    return translate(one, { count: value });
  }
  
  // Some languages like Russian, Polish, Czech have different forms for few (2-4)
  if (value > 1 && value < 5 && many) {
    return translate(many, { count: value });
  }
  
  return translate(other, { count: value });
}

/**
 * Gets the direction (RTL or LTR) for the current locale
 * 
 * @returns 'rtl' for RTL languages, 'ltr' otherwise
 */
export function getDirection(): 'rtl' | 'ltr' {
  const currentLocale = i18n.language;
  return RTL_LOCALES.includes(currentLocale) ? 'rtl' : 'ltr';
}

/**
 * Checks if the current language is RTL
 * 
 * @returns true if current language is RTL, false otherwise
 */
export function isRTL(): boolean {
  return getDirection() === 'rtl';
}